/**
 * Gift With Purchase — sitewide cart controller.
 *
 * On every cart-change this theme dispatches (PUB_SUB_EVENTS.cartUpdate, the
 * same event product-form.js / quick-add-bulk.js / quick-order-list.js / cart.js
 * already publish after every /cart/add.js or /cart/change.js call), this:
 *   1. Fetches sitewide GWP status via the Section Rendering API
 *      (sections/gwp-status.liquid), preserving preview_theme_id from the URL.
 *   2. Compares the raw cart total to the raw threshold setting directly
 *      (single-currency store — no Shopify.currency rate adjustment).
 *   3. Adds the gift (properties: { _gwp: "true" }) if the threshold is met
 *      and the gift isn't already in the cart, caps its quantity at 1, or
 *      removes it if the threshold is no longer met.
 *   4. Publishes PUB_SUB_EVENTS.cartUpdate itself afterwards so any component
 *      that already listens for it (cart-items / cart-drawer-items and their
 *      own getSectionsToRender()/onCartUpdate()) refreshes in place — this is
 *      this theme's existing "sections needing re-render" hook; see cart.js
 *      and cart-drawer.js.
 *   5. Broadcasts a `gwp:status` CustomEvent so the PDP banner (and anything
 *      else) can react to the live cart total.
 */
class GwpManager extends HTMLElement {
  connectedCallback() {
    this.pendingSync = null;
    this.syncQueued = false;

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'gwp-manager') return;
      this.requestSync();
    });
    this.requestSync();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  // Coalesces overlapping cart-update events into a single in-flight sync.
  // Without this, a cart-change event firing while our own add/change call
  // from a previous sync is still in flight starts a second, overlapping
  // status-check-then-mutate cycle — whichever request resolves last wins,
  // which can undo a correct gift add/remove and is the "takes a while, then
  // doesn't fully update" symptom. Any request that arrives while busy just
  // flags one more run for after the current one finishes.
  requestSync() {
    if (this.pendingSync) {
      this.syncQueued = true;
      return this.pendingSync;
    }

    this.pendingSync = this.sync()
      .catch((error) => console.error('GWP: sync failed', error))
      .finally(() => {
        this.pendingSync = null;
        if (this.syncQueued) {
          this.syncQueued = false;
          this.requestSync();
        }
      });

    return this.pendingSync;
  }

  // The Section Rendering API wraps every section's output in
  // <div id="shopify-section-{id}" class="shopify-section">...</div> (the
  // same wrapper cart.js/cart-drawer.js already strip via getSectionInnerHTML
  // when reading the `sections` field of a cart response). gwp-status.liquid
  // renders bare JSON, so unwrap it the same way before parsing.
  extractSectionText(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const wrapper = doc.querySelector('.shopify-section');
    return (wrapper || doc.body).textContent.trim();
  }

  buildStatusUrl() {
    const params = new URLSearchParams();
    params.set('sections', 'gwp-status');
    const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewThemeId) params.set('preview_theme_id', previewThemeId);
    return `${window.location.pathname}?${params.toString()}`;
  }

  // Mirrors CartItems/CartDrawerItems.getSectionsToRender() (assets/cart.js,
  // assets/cart-drawer.js) — the sections this theme already re-renders after
  // any Ajax cart call, so an open cart drawer / cart page stays in sync.
  getSectionsToRender() {
    const sections = ['cart-icon-bubble', 'cart-live-region-text'];
    const mainItems = document.getElementById('main-cart-items');
    if (mainItems) sections.push(mainItems.dataset.id);
    const mainFooter = document.getElementById('main-cart-footer');
    if (mainFooter) sections.push(mainFooter.dataset.id);
    return sections;
  }

  async sync() {
    let status;
    try {
      const response = await fetch(this.buildStatusUrl());
      if (!response.ok) return;
      const data = await response.json();
      status = JSON.parse(this.extractSectionText(data['gwp-status']));
    } catch (error) {
      console.error('GWP: failed to fetch status', error);
      return;
    }

    this.broadcastStatus(status);
    await this.reconcileCart(status);
  }

  async reconcileCart(status) {
    if (!status.enabled || !status.giftVariantId) return;

    if (status.reached && status.giftQty < 1) {
      await this.addGift(status.giftVariantId);
    } else if (status.reached && status.giftQty > 1) {
      await this.changeGiftQty(status.giftLineKey, 1);
    } else if (!status.reached && status.giftQty > 0) {
      await this.changeGiftQty(status.giftLineKey, 0);
    }
  }

  async addGift(giftVariantId) {
    const body = JSON.stringify({
      items: [{ id: giftVariantId, quantity: 1, properties: { _gwp: 'true' } }],
      sections: this.getSectionsToRender(),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(routes.cart_add_url, { ...fetchConfig(), ...{ body } }).then((r) => r.json());
      if (response.status) {
        console.error('GWP: failed to add gift', response);
        return;
      }
      await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gwp-manager', cartData: response });
    } catch (error) {
      console.error('GWP: failed to add gift', error);
    }
  }

  async changeGiftQty(giftLineKey, quantity) {
    if (!giftLineKey) return;
    const body = JSON.stringify({
      id: giftLineKey,
      quantity,
      sections: this.getSectionsToRender(),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(routes.cart_change_url, { ...fetchConfig(), ...{ body } }).then((r) => r.json());
      if (response.status) {
        console.error('GWP: failed to update gift line', response);
        return;
      }
      await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gwp-manager', cartData: response });
    } catch (error) {
      console.error('GWP: failed to update gift line', error);
    }
  }

  broadcastStatus(status) {
    document.dispatchEvent(new CustomEvent('gwp:status', { detail: status }));
  }
}

customElements.define('gwp-manager', GwpManager);

/**
 * PDP progress banner.
 *
 * Rendered server-side (hidden) by snippets/gwp-eligibility.liquid in
 * 'banner' mode, scoped to the viewed product — it only exists in the DOM if
 * that product is in the eligible collection. Listens for `gwp:status` (fired
 * by GwpManager after every cart sync) and reflects the live cart total, not
 * just this product.
 */
class GwpBanner extends HTMLElement {
  connectedCallback() {
    this.onStatus = this.onStatus.bind(this);
    document.addEventListener('gwp:status', this.onStatus);
  }

  disconnectedCallback() {
    document.removeEventListener('gwp:status', this.onStatus);
  }

  onStatus(event) {
    const status = event.detail;
    if (!status || !status.enabled) {
      this.hidden = true;
      return;
    }

    const textEl = this.querySelector('[data-gwp-banner-text]');
    if (!textEl) return;

    const template = status.reached
      ? this.dataset.messageAfter
      : this.dataset.messageBefore;

    const remainingCents = Math.max(Number(this.dataset.threshold) - Number(status.total), 0);
    const amount = (remainingCents / 100).toFixed(2);

    textEl.textContent = template.replace(/\{\{\s*amount\s*\}\}/g, amount);
    this.classList.toggle('gwp-banner--reached', !!status.reached);
    this.hidden = false;
  }
}

if (!customElements.get('gwp-banner')) {
  customElements.define('gwp-banner', GwpBanner);
}
